// Determinism linter for kernel source — RFC-0008 §5.
//
// `verifiable: true` kernels may use only: IEEE-bitwise-specified ops (+ − × ÷, comparison,
// Math.sqrt), the injected `rng`, and the injected `math.*` shim. This linter rejects the
// escapes that silently break cross-node reproducibility:
//   - Math.random            → nondeterministic
//   - Date.* / new Date      → wall-clock nondeterminism
//   - performance.*          → nondeterministic timing
//   - crypto.*               → nondeterministic entropy
//   - Math.<transcendental>  → not cross-engine bitwise (use the injected `math.*`)
//   - outer-scope capture    → a pasted kernel must be self-contained; a free identifier
//                              would freeze at serialisation time and diverge (RFC-0008 §2)
//
// Why a tokenizer and not a full AST parser: the SDK ships with zero runtime dependencies
// beyond the WASM math shim (CLAUDE.md), and this code runs in the browser at kernel-load
// time. We strip comments and string/template/regex literals first so prose and data never
// produce false positives, then scan the remaining identifier and member-access tokens.
// This catches every global named in RFC-0008 §5; its one honest limitation (documented in
// the contract shown to users) is that determinism is *injected*, so the linter is a guard
// against escapes, not a sandbox.

export interface LintFinding {
  /** Stable code so the UI/tests can assert on a specific rule. */
  rule:
    | "math-random"
    | "date"
    | "performance"
    | "crypto"
    | "math-transcendental"
    | "global-random"
    | "outer-scope-capture"
    | "forbidden-global";
  message: string;
  /** 1-based line number in the original source, when locatable. */
  line?: number;
}

// Transcendentals that are NOT IEEE-bitwise-specified — must go through the injected shim.
const TRANSCENDENTALS = new Set([
  "log",
  "exp",
  "sin",
  "cos",
  "tan",
  "pow",
  "atan2",
  "asin",
  "acos",
  "atan",
  "sinh",
  "cosh",
  "tanh",
  "log2",
  "log10",
  "cbrt",
  "expm1",
  "log1p",
  "hypot",
]);

// `Math.*` members that ARE safe (IEEE-bitwise-specified or pure value selection).
const SAFE_MATH = new Set([
  "sqrt",
  "abs",
  "min",
  "max",
  "floor",
  "ceil",
  "round",
  "trunc",
  "sign",
  "PI", // a constant double — same bits everywhere; kernels often need it
  "E",
  "fround",
  "imul",
  "clz32",
]);

// Globals a verifiable kernel may legitimately reference. Anything else free in the module
// scope is treated as outer-scope capture.
const ALLOWED_GLOBALS = new Set([
  // language / values
  "Math",
  "Number",
  "Array",
  "Object",
  "Boolean",
  "String",
  "JSON",
  "Infinity",
  "NaN",
  "undefined",
  "true",
  "false",
  "null",
  "this",
  "Float64Array",
  "Float32Array",
  "Uint32Array",
  "Int32Array",
  "Uint8Array",
  "ArrayBuffer",
  "DataView",
  "Symbol",
  "isNaN",
  "isFinite",
  "parseInt",
  "parseFloat",
  // the kernel contract surface
  "rng",
  "math",
  // module syntax keywords that appear as identifiers in token stream
  "export",
  "default",
  "const",
  "let",
  "var",
  "function",
  "return",
  "if",
  "else",
  "for",
  "while",
  "do",
  "switch",
  "case",
  "break",
  "continue",
  "new",
  "typeof",
  "instanceof",
  "in",
  "of",
  "void",
  "delete",
  "class",
  "extends",
  "super",
  "throw",
  "try",
  "catch",
  "finally",
  "yield",
  "await",
  "async",
  // NOTE: `import` is deliberately NOT allowed — a pasted kernel must be self-contained, and
  // dynamic import() is a code-loading escape. It's flagged via FORBIDDEN_KEYWORDS below so
  // both `import x from …` and `import(…)` are rejected with a clear message.
  "from",
  "as",
  "get",
  "set",
  "static",
]);

// Keywords that are never valid in a self-contained kernel (vs the global *identifiers* in
// HARD_FORBIDDEN_GLOBALS). `import` covers static and dynamic forms.
const FORBIDDEN_KEYWORDS = new Set(["import"]);

const HARD_FORBIDDEN_GLOBALS = new Set([
  "Date",
  "performance",
  "crypto",
  "fetch",
  "XMLHttpRequest",
  "WebSocket",
  "EventSource",
  "localStorage",
  "sessionStorage",
  "indexedDB",
  "caches",
  "navigator",
  "document",
  "window",
  "self",
  "globalThis",
  "parent",
  "top",
  "frames",
  "process",
  "require",
  "module",
  "exports",
  "eval",
  "Function",
  "setTimeout",
  "setInterval",
  "queueMicrotask",
  "requestAnimationFrame",
  // Mesh threat model (slice 2): a kernel runs in a contributor's Worker. These are the
  // remaining escape/abuse surfaces — networking, sub-workers, code loading, shared memory,
  // notifications, and messaging out of the realm. Blocking them is best-effort (the real
  // boundary is the Worker + no-network CSP), but it raises the bar and keeps the kernel
  // contract pure. See docs/mesh-decisions.md D7.
  "importScripts",
  "Worker",
  "SharedWorker",
  "ServiceWorker",
  "MessageChannel",
  "BroadcastChannel",
  "postMessage",
  "WebAssembly",
  "SharedArrayBuffer",
  "Atomics",
  "Notification",
  "navigator",
  "BigInt", // BigInt itself is fine, but flagged because 64-bit nondeterminism hazards
]);

interface Tok {
  /** identifier text */
  id: string;
  /** char index in stripped source */
  pos: number;
  /** true if this identifier directly follows a `.` (i.e. it's a member name) */
  member: boolean;
  /** the identifier immediately before a `.` that precedes this one, if member */
  object?: string;
  line: number;
}

/**
 * Lint kernel source. Returns [] when the kernel is determinism-clean. Each finding carries
 * a fix-it message so a pasted-kernel author learns the contract (RFC-0008 §5: errors with
 * fix-its, not warnings).
 */
export function lintKernelSource(source: string): LintFinding[] {
  const findings: LintFinding[] = [];
  const { code, lineAt } = stripNonCode(source);

  // Collect identifiers declared at module scope so we can tell a free (captured) reference
  // from a locally-bound one. This is a conservative over-approximation: we gather every
  // name that appears in a declaring position (const/let/var/function/class/param) anywhere,
  // which means a kernel can't be tricked into "capture" by shadowing. Good enough — and
  // safe-by-overcollecting means we err toward NOT false-flagging.
  const declared = collectDeclaredNames(code);

  const toks = tokenizeIdentifiers(code, lineAt);

  for (const t of toks) {
    if (t.member) {
      // Member access: flag dangerous object.method combinations.
      const obj = t.object ?? "";
      if (obj === "Math" && t.id === "random") {
        findings.push(f("math-random", "Math.random is nondeterministic. Use the injected `rng` (e.g. `rng.float()`).", t.line));
      } else if (obj === "Math" && TRANSCENDENTALS.has(t.id)) {
        findings.push(f("math-transcendental", `Math.${t.id} is not bitwise-identical across engines. Use the injected shim: \`math.${t.id}(…)\`.`, t.line));
      } else if (obj === "Math" && !SAFE_MATH.has(t.id)) {
        // Any Math member that is neither IEEE-bitwise-specified/safe nor a known
        // transcendental we route to the shim — be conservative, it may be engine-specific.
        findings.push(f("math-transcendental", `Math.${t.id} is not on the cross-engine-safe list. Use the injected \`math\` shim, or rewrite with IEEE-specified ops (+ − × ÷, comparison, Math.sqrt).`, t.line));
      } else if (obj === "Date") {
        findings.push(f("date", "Date.* is wall-clock nondeterminism and is not allowed in a kernel.", t.line));
      } else if (obj === "performance") {
        findings.push(f("performance", "performance.* is nondeterministic. A kernel's result must depend only on (kernel, seed, N).", t.line));
      } else if (obj === "crypto") {
        findings.push(f("crypto", "crypto.* is a nondeterministic entropy source. Use the injected `rng`.", t.line));
      }
      continue;
    }

    // Bareword identifier (not a member, not a declaration site — see below).
    if (t.id === "Math" || t.id === "rng" || t.id === "math") continue;

    if (FORBIDDEN_KEYWORDS.has(t.id)) {
      findings.push(f("forbidden-global", "`import` is not allowed — a kernel must be self-contained (no static or dynamic imports). Inline everything it needs.", t.line));
      continue;
    }

    if (HARD_FORBIDDEN_GLOBALS.has(t.id)) {
      // Map a few to their dedicated rule codes for precise messages/tests.
      if (t.id === "Date") {
        findings.push(f("date", "`Date` is wall-clock nondeterminism and is not allowed in a kernel.", t.line));
      } else if (t.id === "performance") {
        findings.push(f("performance", "`performance` is nondeterministic and is not allowed in a kernel.", t.line));
      } else if (t.id === "crypto") {
        findings.push(f("crypto", "`crypto` is a nondeterministic entropy source. Use the injected `rng`.", t.line));
      } else {
        findings.push(f("forbidden-global", `\`${t.id}\` is not available to a kernel (it is an I/O or nondeterminism surface).`, t.line));
      }
      continue;
    }

    // Free identifier that is neither an allowed global nor declared in this module →
    // outer-scope capture. A pasted kernel must be self-contained (RFC-0008 §2).
    if (!ALLOWED_GLOBALS.has(t.id) && !declared.has(t.id)) {
      findings.push(f("outer-scope-capture", `\`${t.id}\` is not declared in this kernel and is not part of the kernel contract (rng, math). A kernel must be self-contained — outer-scope references freeze at load time and break reproducibility.`, t.line));
    }
  }

  // De-duplicate identical (rule,line,message) findings (a name used many times).
  return dedupe(findings);
}

function f(rule: LintFinding["rule"], message: string, line: number): LintFinding {
  return { rule, message, line };
}

/**
 * Does the kernel call a transcendental through the injected `math.*` shim
 * (e.g. `math.log`, `math.cos`)? Such calls are legal (they ride the future fdlibm path),
 * but pre-M3 the shim is native Math, so the kernel is only engine-specific — it CANNOT
 * honestly claim `verifiable: true` until the shim is cross-engine. The loader uses this to
 * downgrade an over-eager author flag. (`math.sqrt` is IEEE-bitwise-specified, so it does
 * NOT count.)
 */
export function usesShimTranscendentals(source: string): boolean {
  const { code } = stripNonCode(source);
  // simple member scan: `math` `.` <transcendental>
  const re = /\bmath\s*\.\s*([A-Za-z_$][\w$]*)/g;
  for (let m; (m = re.exec(code)); ) {
    if (TRANSCENDENTALS.has(m[1]!)) return true;
  }
  return false;
}

function dedupe(findings: LintFinding[]): LintFinding[] {
  const seen = new Set<string>();
  const out: LintFinding[] = [];
  for (const x of findings) {
    const key = `${x.rule}|${x.line}|${x.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(x);
    }
  }
  return out;
}

// ── Lexical helpers ──────────────────────────────────────────────────────────

/**
 * Remove comments and string/template/regex literal CONTENT (replacing each with spaces so
 * positions are preserved), and return a function mapping a char index to its 1-based line.
 * We keep line breaks intact for accurate line numbers.
 */
function stripNonCode(src: string): { code: string; lineAt: (pos: number) => number } {
  const out: string[] = [];
  let i = 0;
  const n = src.length;
  // Track whether a `/` can begin a regex by remembering the last significant token.
  let prevSignificant = "";

  const push = (s: string) => out.push(s);
  const blank = (len: number) => out.push(" ".repeat(len));

  while (i < n) {
    const c = src[i]!;
    const c2 = src[i + 1];

    // line comment
    if (c === "/" && c2 === "/") {
      let j = i;
      while (j < n && src[j] !== "\n") j++;
      blank(j - i);
      i = j;
      continue;
    }
    // block comment
    if (c === "/" && c2 === "*") {
      let j = i + 2;
      while (j < n && !(src[j] === "*" && src[j + 1] === "/")) j++;
      j = Math.min(n, j + 2);
      // preserve newlines inside the block for line counting
      for (let k = i; k < j; k++) out.push(src[k] === "\n" ? "\n" : " ");
      i = j;
      continue;
    }
    // string literals
    if (c === '"' || c === "'") {
      let j = i + 1;
      while (j < n && src[j] !== c) {
        if (src[j] === "\\") j++;
        j++;
      }
      j = Math.min(n, j + 1);
      for (let k = i; k < j; k++) out.push(src[k] === "\n" ? "\n" : " ");
      i = j;
      prevSignificant = "x"; // a string is a value
      continue;
    }
    // template literal — blank content but we conservatively also blank ${…} (so an
    // identifier hidden in a template expression is not scanned). Acceptable: templates are
    // not part of a numeric kernel's hot path.
    if (c === "`") {
      let j = i + 1;
      while (j < n && src[j] !== "`") {
        if (src[j] === "\\") j++;
        j++;
      }
      j = Math.min(n, j + 1);
      for (let k = i; k < j; k++) out.push(src[k] === "\n" ? "\n" : " ");
      i = j;
      prevSignificant = "x";
      continue;
    }
    // regex literal: `/` in a position where a value can begin
    if (c === "/" && canBeRegex(prevSignificant)) {
      let j = i + 1;
      let inClass = false;
      while (j < n) {
        const ch = src[j];
        if (ch === "\\") {
          j += 2;
          continue;
        }
        if (ch === "[") inClass = true;
        else if (ch === "]") inClass = false;
        else if (ch === "/" && !inClass) break;
        else if (ch === "\n") break; // unterminated; bail
        j++;
      }
      // consume flags
      j = Math.min(n, j + 1);
      while (j < n && /[a-z]/i.test(src[j]!)) j++;
      for (let k = i; k < j; k++) out.push(src[k] === "\n" ? "\n" : " ");
      i = j;
      prevSignificant = "x";
      continue;
    }

    push(c);
    if (!/\s/.test(c)) prevSignificant = c;
    i++;
  }

  const code = out.join("");
  // Precompute line starts for O(log n) lookup.
  const lineStarts: number[] = [0];
  for (let k = 0; k < code.length; k++) if (code[k] === "\n") lineStarts.push(k + 1);
  const lineAt = (pos: number): number => {
    let lo = 0;
    let hi = lineStarts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (lineStarts[mid]! <= pos) lo = mid;
      else hi = mid - 1;
    }
    return lo + 1;
  };
  return { code, lineAt };
}

function canBeRegex(prev: string): boolean {
  // After an identifier char, ), ], or a number, `/` is division; otherwise it can start a
  // regex. Conservative: default to regex when unsure (we only blank content either way).
  if (prev === "") return true;
  return !/[\w)\]$]/.test(prev);
}

const ID_START = /[A-Za-z_$]/;
const ID_CHAR = /[A-Za-z0-9_$]/;

/** Pull identifier tokens, marking member accesses and their object. */
function tokenizeIdentifiers(code: string, lineAt: (p: number) => number): Tok[] {
  const toks: Tok[] = [];
  const n = code.length;
  let i = 0;
  let lastId: string | undefined;
  let sawDot = false;

  while (i < n) {
    const c = code[i]!;
    if (c === ".") {
      sawDot = true;
      i++;
      continue;
    }
    // Skip numeric literals whole (incl. separators `1_000`, hex, exponents) so digits and
    // `_000`-style fragments are never mistaken for identifiers.
    if (/[0-9]/.test(c)) {
      let j = i + 1;
      while (j < n && /[0-9a-fA-FxXoObBeE._]/.test(code[j]!)) {
        // allow a sign only directly after an exponent marker
        j++;
      }
      lastId = undefined;
      sawDot = false;
      i = j;
      continue;
    }
    if (ID_START.test(c)) {
      let j = i + 1;
      while (j < n && ID_CHAR.test(code[j]!)) j++;
      const id = code.slice(i, j);
      const member = sawDot;
      // Look ahead past whitespace: an identifier directly followed by `:` is an object
      // property key (or a label) — a binding name, not a reference. Skip it.
      let k = j;
      while (k < n && /\s/.test(code[k]!)) k++;
      const isPropertyKey = code[k] === ":" && code[k + 1] !== ":";
      if (!isPropertyKey) {
        toks.push({ id, pos: i, member, object: member ? lastId : undefined, line: lineAt(i) });
      }
      lastId = id;
      sawDot = false;
      i = j;
      continue;
    }
    // any other non-space char resets "previous identifier" unless it's whitespace
    if (!/\s/.test(c)) {
      if (c !== ")" && c !== "]") lastId = undefined;
      sawDot = false;
    }
    i++;
  }
  return toks;
}

/** Conservatively collect every name introduced by a declaration in the module. */
function collectDeclaredNames(code: string): Set<string> {
  const names = new Set<string>();

  // const/let/var X , function X, class X (first declarator / name)
  const declRe = /\b(?:const|let|var|function\*?|class)\s+([A-Za-z_$][\w$]*)/g;
  for (let m; (m = declRe.exec(code)); ) names.add(m[1]!);

  // Multi-declarator continuations: `let s = 0, n = 0, k;` — collect every name that
  // follows a comma in a declaration list. Over-collect: any `, name` where name is then
  // followed by `=`, `,`, or `;` is treated as a local binding.
  const multiDeclRe = /,\s*([A-Za-z_$][\w$]*)\s*(?=[=,;])/g;
  for (let m; (m = multiDeclRe.exec(code)); ) names.add(m[1]!);

  // Object method shorthand and class methods: `name(params) {` — the method name is a
  // binding, not a free reference. (We err toward over-collecting; a kernel calling its own
  // helper method is legitimate.)
  const methodRe = /(^|[{,;]|\b(?:get|set|static|async)\b)\s*([A-Za-z_$][\w$]*)\s*\([^()]*\)\s*\{/g;
  for (let m; (m = methodRe.exec(code)); ) names.add(m[2]!);

  // Destructuring + multi-declarator + assignment targets and ALL parameter names: rather
  // than fully parse, collect every identifier that appears immediately before `=`, `,`,
  // `)`, `}`, `]`, `:` inside what looks like a binding, AND every identifier inside any
  // parameter list `(...)` that precedes `=>` or `{`. To stay safe (avoid false capture
  // flags) we over-collect: gather every identifier that is ever followed by `=>` params or
  // appears in arrow/function parameter position.
  // Function/arrow parameter lists:
  const paramListRe = /(?:function\*?\s*[A-Za-z_$\w]*\s*|=>\s*|(?<=\b(?:if|for|while|switch|catch)\b)\s*|\)\s*=>|\b)\(([^()]*)\)\s*(?:=>|\{)/g;
  for (let m; (m = paramListRe.exec(code)); ) {
    for (const id of m[1]!.match(/[A-Za-z_$][\w$]*/g) ?? []) names.add(id);
  }
  // Single-identifier arrow params: `x =>`
  const singleArrowRe = /(^|[^.\w$])([A-Za-z_$][\w$]*)\s*=>/g;
  for (let m; (m = singleArrowRe.exec(code)); ) names.add(m[2]!);

  // Destructuring binds and object/array patterns in declarations: collect identifiers
  // inside `{...}` / `[...]` that follow const|let|var.
  const destrRe = /\b(?:const|let|var)\s*([\[{][^=;]*?[\]}])\s*=/g;
  for (let m; (m = destrRe.exec(code)); ) {
    for (const id of m[1]!.match(/[A-Za-z_$][\w$]*/g) ?? []) names.add(id);
  }

  // Loop variables `for (const x of …)` already caught by declRe; `catch (e)` param:
  const catchRe = /\bcatch\s*\(\s*([A-Za-z_$][\w$]*)/g;
  for (let m; (m = catchRe.exec(code)); ) names.add(m[1]!);

  return names;
}
